import React, { useState, useCallback, useEffect, useRef } from 'react';
import { parseBeautyHunters } from '../utils/parseBeautyHunters';
import { useToast } from './ToastManager';
import useScrollLock from '../utils/useScrollLock';
import './ImportExcelModal.css';

const API_URL      = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const ATTR_NAME    = 'Tamaño/Tono';
const BH_PARENT    = 'beauty hunters'; // lowercase match

export default function ImportExcelModal({ onClose, onSaved }) {
  useScrollLock();
  const { showToast } = useToast();
  const [stage,    setStage]    = useState('upload');  // upload | preview | importing | done
  const [parsed,   setParsed]   = useState(null);      // { products, skippedRows }
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [result,   setResult]   = useState(null);      // { created, newCategories, errors }
  const [dragOver, setDragOver] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const fileRef = useRef(null);

  // ESC to close (only in upload/preview stages)
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape' && stage !== 'importing') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, stage]);

  // ── File processing ────────────────────────────────────────────────────────
  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) {
      showToast({ variant: 'error', message: 'Selecciona un archivo .xlsx (Excel)' });
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const data   = parseBeautyHunters(buffer);
      if (data.products.length === 0) {
        showToast({ variant: 'warning', message: 'No se detectaron productos. Verifica el formato Beauty Hunters.' });
        return;
      }
      setParsed(data);
      setStage('preview');
    } catch (err) {
      showToast({ variant: 'error', message: 'Error al leer el archivo: ' + err.message });
    }
  }, [showToast]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  // ── Import ─────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setStage('importing');
    setProgress({ current: 0, total: 0, message: 'Iniciando…' });

    const errors        = [];
    let   created       = 0;
    let   newCategories = 0;

    const tick = (msg, current, total) =>
      setProgress({ current, total, message: msg });

    try {
      // 1. Load current categories
      tick('Cargando categorías…', 0, 1);
      const catRes = await fetch(`${API_URL}/api/categories?form=1`);
      if (!catRes.ok) throw new Error('No se pudieron cargar las categorías');
      const allCats = await catRes.json();

      // 2. Find "Beauty Hunters" parent category
      const bhParent = allCats.find(
        (c) => c.parent === 0 && c.name.toLowerCase().includes(BH_PARENT)
      );
      if (!bhParent) {
        throw new Error(
          'No se encontró la categoría "Beauty Hunters" en WooCommerce.\n' +
          'Créala manualmente desde WooCommerce → Productos → Categorías y vuelve a intentarlo.'
        );
      }

      // 3. Resolve / create brand subcategories
      const uniqueBrands = [...new Set(parsed.products.map((p) => p.marca))];
      const totalOps =
        uniqueBrands.length +
        parsed.products.reduce((s, p) => s + 1 + p.variaciones.length, 0);
      let step = 0;

      const brandCatMap = {}; // brandName → catId

      for (const brand of uniqueBrands) {
        step++;
        tick(`Verificando categoría: ${brand}…`, step, totalOps);

        const existing = allCats.find(
          (c) =>
            c.parent === bhParent.id &&
            c.name.toLowerCase() === brand.toLowerCase()
        );
        if (existing) {
          brandCatMap[brand] = existing.id;
        } else {
          const res  = await fetch(`${API_URL}/api/categories`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ name: brand, parent: bhParent.id }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(`No se pudo crear la categoría "${brand}": ${data.error}`);
          brandCatMap[brand] = data.id;
          newCategories++;
        }
      }

      // 4. Create products + variations
      for (const product of parsed.products) {
        step++;
        tick(`Creando: ${product.nombre}…`, step, totalOps);

        const catId   = brandCatMap[product.marca];
        const prodRes = await fetch(`${API_URL}/api/products`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            name:        product.nombre,
            description: '',
            type:        'variable',
            status:      'draft',
            categories:  [catId],
            images:      [],
            attributes:  [{
              name:    ATTR_NAME,
              options: [...new Set(product.variaciones.map((v) => v.tono))],
            }],
          }),
        });
        const prodData = await prodRes.json();

        if (!prodRes.ok) {
          errors.push(`${product.nombre}: ${prodData.error || 'Error al crear producto'}`);
          step += product.variaciones.length;
          tick(`Error en ${product.nombre}, continuando…`, step, totalOps);
          continue;
        }

        created++;
        const pid = prodData.id;

        for (const v of product.variaciones) {
          step++;
          tick(`  ↳ variación: ${v.tono}`, step, totalOps);
          const varRes = await fetch(`${API_URL}/api/products/${pid}/variations`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              regular_price:  v.precio,
              stock_quantity: v.stock,
              attributes:     [{ name: ATTR_NAME, option: v.tono }],
            }),
          });
          if (!varRes.ok) {
            const varData = await varRes.json().catch(() => ({}));
            errors.push(`  • ${product.nombre} / ${v.tono}: ${varData.error || 'Error al crear variación'}`);
          }
        }
      }

      setResult({ created, newCategories, errors, skippedRows: parsed.skippedRows });
      setStage('done');
      onSaved && onSaved();
    } catch (err) {
      errors.push('Error crítico: ' + err.message);
      setResult({ created, newCategories, errors, skippedRows: parsed.skippedRows });
      setStage('done');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const canClose = stage !== 'importing';

  return (
    <div
      className="iem-overlay"
      onClick={canClose ? onClose : undefined}
    >
      <div className="iem" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="iem-header">
          <div>
            <h2 className="iem-title">Importar desde Excel</h2>
            <span className="iem-subtitle">Formato Beauty Hunters</span>
          </div>
          {canClose && (
            <button className="iem-close" onClick={onClose}>✕</button>
          )}
        </div>

        {/* ── STAGE: UPLOAD ── */}
        {stage === 'upload' && (
          <div className="iem-body">
            <div
              className={`iem-dropzone${dragOver ? ' iem-dropzone--over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <span className="iem-drop-icon">📊</span>
              <p className="iem-drop-title">
                {dragOver ? 'Suelta el archivo aquí' : 'Arrastra tu archivo Excel aquí'}
              </p>
              <p className="iem-drop-sub">o haz clic para seleccionarlo</p>
              <span className="iem-drop-ext">.xlsx</span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => processFile(e.target.files[0])}
            />

            <div className="iem-format-note">
              <strong>Formato esperado:</strong> hoja &ldquo;Catálogo de ventas&rdquo; con columnas
              Marca, Producto, Tamaño / Tono, Precio venta, Unidades.
              Las columnas de costos y competencia serán ignoradas.
            </div>
          </div>
        )}

        {/* ── STAGE: PREVIEW ── */}
        {stage === 'preview' && parsed && (
          <div className="iem-body">
            <div className="iem-preview-header">
              <span className="iem-badge iem-badge--ok">
                {parsed.products.length} {parsed.products.length === 1 ? 'producto detectado' : 'productos detectados'}
              </span>
              {parsed.skippedRows.length > 0 && (
                <span className="iem-badge iem-badge--warn">
                  {parsed.skippedRows.length} {parsed.skippedRows.length === 1 ? 'fila ignorada' : 'filas ignoradas'}
                </span>
              )}
            </div>

            <div className="iem-warn-box">
              ⚠ Estos productos se crearán <strong>sin imagen ni descripción</strong>.
              Complétalos después desde <em>Editar Producto</em>.
            </div>

            <div className="iem-draft-box">
              🔒 Los productos se crearán como <strong>Borrador</strong> y no serán visibles en la
              tienda hasta que completes su descripción, imágenes, y los publiques manualmente desde
              el módulo de <em>Editar Producto</em>.
            </div>

            <div className="iem-table-wrap">
              <table className="iem-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Subcategoría (Marca)</th>
                    <th>Variaciones</th>
                    <th>Total unid.</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.products.map((p, i) => (
                    <tr key={i}>
                      <td className="iem-td-name">{p.nombre}</td>
                      <td>
                        <span className="iem-brand-chip">{p.marca}</span>
                      </td>
                      <td>
                        <div className="iem-var-list">
                          {p.variaciones.map((v, j) => (
                            <span key={j} className="iem-var-chip">
                              {v.tono}
                              <span className="iem-var-price">${Number(v.precio).toLocaleString('es-CO')}</span>
                              <span className="iem-var-stock">×{v.stock}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="iem-td-total">
                        {p.variaciones.reduce((s, v) => s + v.stock, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parsed.skippedRows.length > 0 && (
              <div className="iem-skipped">
                <button
                  className="iem-skipped-toggle"
                  onClick={() => setShowSkipped((v) => !v)}
                >
                  {showSkipped ? '▲ Ocultar' : '▼ Ver'} filas ignoradas ({parsed.skippedRows.length})
                </button>
                {showSkipped && (
                  <ul className="iem-skipped-list">
                    {parsed.skippedRows.map((r, i) => (
                      <li key={i}>Fila {r.row}: {r.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="iem-footer">
              <button
                className="iem-btn-cancel"
                onClick={() => { setParsed(null); setStage('upload'); }}
              >
                ← Cambiar archivo
              </button>
              <button className="iem-btn-primary" onClick={handleImport}>
                Confirmar importación →
              </button>
            </div>
          </div>
        )}

        {/* ── STAGE: IMPORTING ── */}
        {stage === 'importing' && (
          <div className="iem-body iem-body--center">
            <div className="iem-spinner-lg" />
            <p className="iem-progress-msg">{progress.message}</p>
            {progress.total > 0 && (
              <>
                <div className="iem-progress-bar-wrap">
                  <div
                    className="iem-progress-bar-fill"
                    style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  />
                </div>
                <p className="iem-progress-count">
                  {progress.current} / {progress.total} operaciones
                </p>
              </>
            )}
          </div>
        )}

        {/* ── STAGE: DONE ── */}
        {stage === 'done' && result && (
          <div className="iem-body">
            <div className={`iem-result-header${result.errors.length > 0 ? ' iem-result-header--warn' : ' iem-result-header--ok'}`}>
              {result.errors.length === 0 ? '✓ Importación completada' : '⚠ Importación completada con errores'}
            </div>

            <div className="iem-result-stats">
              <div className="iem-stat">
                <span className="iem-stat-num">{result.created}</span>
                <span className="iem-stat-label">productos creados</span>
              </div>
              <div className="iem-stat">
                <span className="iem-stat-num">{result.newCategories}</span>
                <span className="iem-stat-label">subcategorías nuevas</span>
              </div>
              {result.errors.length > 0 && (
                <div className="iem-stat iem-stat--err">
                  <span className="iem-stat-num">{result.errors.length}</span>
                  <span className="iem-stat-label">errores</span>
                </div>
              )}
            </div>

            {result.created > 0 && (
              <div className="iem-reminder">
                📷 Recuerda agregar <strong>fotos y descripciones</strong> a los productos importados
                desde el módulo de <em>Editar Producto</em> del Catálogo.
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="iem-errors">
                <p className="iem-errors-title">Errores:</p>
                <ul className="iem-errors-list">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {result.skippedRows?.length > 0 && (
              <div className="iem-skipped">
                <button
                  className="iem-skipped-toggle"
                  onClick={() => setShowSkipped((v) => !v)}
                >
                  {showSkipped ? '▲ Ocultar' : '▼ Ver'} filas ignoradas del Excel ({result.skippedRows.length})
                </button>
                {showSkipped && (
                  <ul className="iem-skipped-list">
                    {result.skippedRows.map((r, i) => (
                      <li key={i}>Fila {r.row}: {r.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="iem-footer">
              <button className="iem-btn-primary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
